import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";

const FILE_LIST_ENDPOINT = "/api/pos_reco/get_pos_file_upload_logs";
const FILE_RERUN_ENDPOINT = "/cash-card-upload/rerun";

const dialogGridSx = {
	height: "100%",
	width: "100%",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: 2,
	bgcolor: "background.paper",
	"& .MuiDataGrid-toolbarContainer": {
		padding: "10px 12px 6px",
		borderBottom: "1px solid",
		borderColor: "divider",
		gap: 1,
	},
	"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
		minWidth: "auto",
		padding: "4px 8px",
		borderRadius: 1.5,
		color: "text.secondary",
		fontSize: "0.75rem",
		fontWeight: 500,
	},
	"& .MuiDataGrid-toolbarContainer .MuiInputBase-root": {
		borderRadius: 1.5,
		backgroundColor: "grey.50",
	},
	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: "grey.50",
		borderBottom: "1px solid",
		borderColor: "divider",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 500,
		color: "text.secondary",
		textTransform: "uppercase",
		fontSize: "0.7rem",
		letterSpacing: "0.4px",
	},
	"& .MuiDataGrid-cell": {
		borderBottom: "1px solid",
		borderColor: "divider",
		fontSize: "0.82rem",
		color: "text.primary",
	},
	"& .MuiDataGrid-columnSeparator": {
		display: "none",
	},
	"& .MuiDataGrid-footerContainer": {
		borderTop: "1px solid",
		borderColor: "divider",
		minHeight: 44,
	},
};

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString();
};

const FileDetailsDialogue = ({ open, onClose }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [rerunId, setRerunId] = useState(null);

	const fetchFiles = async () => {
		setLoading(true);
		try {
			const response = await axios.get(FILE_LIST_ENDPOINT);
			const data = Array.isArray(response.data) ? response.data : response.data?.files || response.data?.result || [];
			setRows(
				data.map((row, index) => ({
					...row,
					id: row?.id ?? index + 1,
				})),
			);
		} catch (error) {
			setRows([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!open) return;

		fetchFiles();
	}, [open]);

	const handleRerun = async (row) => {
		setRerunId(row.id);
		try {
			await pythonVmInstance.post(FILE_RERUN_ENDPOINT, {
				id: row.id,
				file_type: row.file_type,
				file_name: row.file_name,
			});
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Rerun triggered successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			await fetchFiles();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to rerun file",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setRerunId(null);
		}
	};

	const columns = useMemo(
		() => [
			{ field: "file_type", headerName: "File Type", minWidth: 160, flex: 0.9 },
			{ field: "file_name", headerName: "File Name", minWidth: 260, flex: 1.3 },
			{
				field: "date",
				headerName: "Date",
				minWidth: 180,
				flex: 0.9,
				valueGetter: (value) => (value ? new Date(value) : null),
				renderCell: (params) => formatDateTime(params.row.date),
			},
			{ field: "reason", headerName: "Reason", minWidth: 220, flex: 1 },
			{
				field: "status",
				headerName: "Status",
				minWidth: 130,
				flex: 0.7,
				renderCell: (params) => {
					const status = params.value ? String(params.value).toUpperCase() : "UNKNOWN";
					let color = "default";
					if (status.includes("SUCCESS") || status.includes("COMPLETED")) color = "success";
					else if (status.includes("PENDING") || status.includes("IN_PROGRESS")) color = "warning";
					else if (status.includes("FAILED") || status.includes("ERROR")) color = "error";
					return <Chip label={status} color={color} size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
				},
			},
			{
				field: "actions",
				headerName: "Rerun",
				minWidth: 130,
				sortable: false,
				filterable: false,
				align: "center",
				headerAlign: "center",
				renderCell: (params) => (
					<Button
						size="small"
						variant="text"
						startIcon={
							rerunId === params.row.id ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<RefreshRoundedIcon fontSize="small" />
							)
						}
						onClick={() => handleRerun(params.row)}
						disabled={rerunId === params.row.id}
						sx={{ textTransform: "none", minWidth: "auto" }}>
						Rerun
					</Button>
				),
			},
		],
		[rerunId],
	);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Uploaded Files
					</Typography>
					<Typography variant="body2" color="text.secondary">
						View all uploaded files and their processing status.
					</Typography>
				</Box>
				<IconButton onClick={onClose}>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ pb: 3 }}>
				<Box sx={{ height: "70vh", width: "100%", pt: 1 }}>
					<DataGrid
						rows={rows}
						columns={columns}
						loading={loading}
						disableRowSelectionOnClick
						initialState={{
							pagination: { paginationModel: { pageSize: 25 } },
						}}
						pageSizeOptions={[10, 25, 50, 100]}
						slots={{
							toolbar: GridToolbar,
							noRowsOverlay: () => (
								<Typography variant="body2" sx={{ mt: 2 }}>
									No uploaded files found
								</Typography>
							),
						}}
						slotProps={{
							toolbar: { showQuickFilter: true },
						}}
						sx={dialogGridSx}
					/>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default FileDetailsDialogue;
